import React from "react";
import { connect } from "react-redux";
import { ApplicationState } from "../../store/ApplicationStore";
import { FiltersData as BookingTolerancesFiltersData } from "../../store/state/BookingTolerances/FiltersData";
import { TableData as BookingTolerancesTableData } from "../../store/state/BookingTolerances/TableData";
import { BookingTolerancesFiltersActions as ActionClassFilters } from "../../store/actions/BookingTolerances/FiltersActions";
import { DEFAULT_PATHS } from "../../utils/ApiUtils";
import {
  Autosuggests,
  BindProperties,
  Checkboxes,
  ConfigurationMethods,
  EFFECTIVE,
  Inputs,
  SectionKeys,
  Selects,
} from "../../utils/Constants";
import { IHistory } from "../../store/state/History";
import { RouteComponentProps } from "react-router";
import FiltersTableLayoutsWrapper from "../../layout/FiltersTableLayouts/FiltersTableLayoutsWrapper";
import { IRow } from "../../layout/EditorLayout/EditorLayout";
import { CATEGORIES } from "../../utils/PermissionsChecker";
import { ColumnProps } from "antd/lib/table";
import Tooltip from "antd/lib/tooltip";
import { BookingTolerancesTableActions as ActionClassTable } from "../../store/actions/BookingTolerances/TableActions";
import { BookingTolerancesEditorActions as ActionClassEditor } from "../../store/actions/BookingTolerances/EditorActions";
import TableFormatter from "../../utils/TableFormatter";
import { EntityType } from "../../integrated-client";

interface IBookingTolerancesProps {
  bookingTolerancesFiltersData: BookingTolerancesFiltersData;
  bookingTolerancesTableData: BookingTolerancesTableData;
}

const BookingTolerances: React.FC<
  IBookingTolerancesProps & IHistory & RouteComponentProps
> = (props) => {
  const { bookingTolerancesFiltersData, bookingTolerancesTableData, history } =
    props;

  const rows: Array<IRow> = [
    {
      index: 0,
      items: [
        {
          id: Selects.BrandAndMarket as never,
          type: "select",
          label: "Brand and Market",
          allowClear: true,
          configurationProperty: "brandsAndMarkets",
        },
        {
          id: Selects.DistributionChannel as never,
          type: "select",
          label: "Distribution Channel",
          allowClear: true,
          configurationProperty: "distributionChannels",
        },
        {
          id: Selects.Supplier as never,
          type: "select",
          label: "Supplier",
          allowClear: true,
          configurationProperty: "suppliers",
        },
        {
          id: Autosuggests.Carrier as never,
          type: "autoSuggest",
          label: "Carrier",
          allowClear: true,
        },
      ],
    },
    {
      index: 0,
      items: [
        {
          id: Selects.Relevance as never,
          type: "select",
          label: "Relevance",
          allowClear: true,
          configurationProperty: "relevances",
        },
        {
          id: Inputs.Description as never,
          type: "input",
          label: "Description",
          allowClear: true,
        },
        {
          id: Checkboxes.ShowDisabled as never,
          type: "checkbox",
          label: "Show Disabled",
          allowClear: true,
        },
        {
          id: Checkboxes.ShowDeleted as never,
          type: "checkbox",
          label: "Show Deleted",
          allowClear: true,
        },
      ],
    },
  ];
  const columns: Array<ColumnProps<any>> = [
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      ellipsis: true,
      render: (value: any) => (
        <Tooltip placement="topLeft" title={value}>
          <span>{value}</span>
        </Tooltip>
      ),
    },
    {
      title: "Comment",
      dataIndex: "comment",
      key: "comment",
      ellipsis: true,
      render: (value: any) => (
        <Tooltip placement="topLeft" title={value}>
          <span>{value}</span>
        </Tooltip>
      ),
    },
    {
      title: "Brand and Market",
      dataIndex: "brandAndMarket",
      key: "brandAndMarket",
      ellipsis: true,
      render: (value: any) => (
        <Tooltip
          placement="topLeft"
          title={`${value.brand.label} (${value.market.label})`}
        >
          <span>{`${value.brand.label} (${value.market.label})`}</span>
        </Tooltip>
      ),
    },
    {
      title: "Distribution Channel",
      dataIndex: "distributionChannels",
      key: "distributionChannel",
      ellipsis: true,
      render: (value: any) => {
        const items = TableFormatter.formatSecondaryValuesArrayIfFalse({
          items: value,
          isSecondaryFieldName: EFFECTIVE,
          labelFieldName: "label",
        });
        return (
          <Tooltip placement="topLeft" title={items}>
            <span>{items}</span>
          </Tooltip>
        );
      },
    },
    {
      title: "Supplier",
      dataIndex: "suppliers",
      key: "supplier",
      ellipsis: true,
      render: (value: any) => {
        const items = TableFormatter.formatSecondaryValuesArrayIfFalse({
          items: value,
          isSecondaryFieldName: EFFECTIVE,
          labelFieldName: "label",
        });
        return (
          <Tooltip placement="topLeft" title={items}>
            <span>{items}</span>
          </Tooltip>
        );
      },
    },
    {
      title: "Relevance",
      dataIndex: "relevance",
      key: "relevance",
      ellipsis: true,
      sorter: true,
      render: (value: any) => (
        <Tooltip placement="topLeft" title={value}>
          <span>{value}</span>
        </Tooltip>
      ),
    },
    {
      title: "Disabled",
      dataIndex: "disabled",
      key: "disabled",
      ellipsis: true,
      render: (value: any) => (
        <Tooltip placement="topLeft" title={String(value)}>
          <span>{String(value)}</span>
        </Tooltip>
      ),
    },
    {
      title: "Deleted",
      dataIndex: "deleted",
      key: "deleted",
      ellipsis: true,
      render: (value: any) => (
        <Tooltip placement="topLeft" title={String(value)}>
          <span>{String(value)}</span>
        </Tooltip>
      ),
    },
  ];
  return (
    <FiltersTableLayoutsWrapper
      layoutFilters={{
        rows,
        filtersData: bookingTolerancesFiltersData,
        actionClassFilters: ActionClassFilters,
        actionClassEditor: ActionClassEditor,
        configurationMethod: ConfigurationMethods.BookingTolerancesList,
        configurationBindingItems: [
          {
            key: "brandAndMarkets",
            id: Selects.BrandAndMarket,
            field: "label",
          },
        ],
      }}
      layoutTable={{
        columns: columns,
        tableData: bookingTolerancesTableData,
        actionClassTable: ActionClassTable,
        historyType: EntityType.BookingToleranceUpdate,
        withCopy: true,
        withDelete: true,
      }}
      sectionKey={SectionKeys.BookingTolerances}
      path={`${DEFAULT_PATHS.BOOKING_TOLERANCES}`}
      menuListProperty={"brandsAndMarkets" as never}
      actionClassEditor={ActionClassEditor}
      bindProperty={BindProperties.BrandAndMarket}
      category={CATEGORIES.BOOKING_TOLERANCES}
      history={history}
    />
  );
};

const mapStateToProps = function (
  state: ApplicationState
): IBookingTolerancesProps {
  const {
    filtersData: bookingTolerancesFiltersData,
    tableData: bookingTolerancesTableData,
  } = state.bookingTolerances;
  return { bookingTolerancesFiltersData, bookingTolerancesTableData };
};

export default connect(mapStateToProps)(BookingTolerances);
